import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { BookUser, Download, Utensils } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import apiClient from '@/api/client'
import type { UseReportRange } from '@/hooks/useReportRange'
import type { HouseAccount } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { toastHelpers } from '@/lib/toast-helpers'
import { useHouseAccountsEnabled } from '@/hooks/useHouseAccountsEnabled'

interface Props {
  range: UseReportRange
}

function currentMonth(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

const ENTRY_LABEL: Record<string, string> = {
  charge: 'Charge',
  charge_reversal: 'Refund',
  settlement: 'Payment',
  adjustment: 'Adjustment',
}

/**
 * F-MM-N30 — staff meal allowances and customer tabs, for reconciliation.
 *
 *   • Staff meals by month: what each person ate, how much the allowance
 *     covered, and every meal that went over (date, order, time, approver).
 *     Staff meals are not revenue; their food cost is the "Staff meals (at
 *     cost)" line in the P&L.
 *   • Tab aging: who owes what, and how old the oldest unpaid bill is.
 *   • Statements: one account's running balance for the period above.
 */
export function HouseAccountsTab({ range }: Props) {
  const { formatCurrency } = useCurrency()
  const gate = useHouseAccountsEnabled()
  const navigate = useNavigate()
  const [month, setMonth] = useState(currentMonth())
  const [statementFor, setStatementFor] = useState<HouseAccount | null>(null)

  const staff = useQuery({
    queryKey: ['house-accounts', 'staff-month', month],
    queryFn: () => apiClient.getStaffMonthReport(month),
    enabled: gate.staff && /^\d{4}-\d{2}$/.test(month),
  })
  const aging = useQuery({
    queryKey: ['house-accounts', 'tab-aging'],
    queryFn: () => apiClient.getTabAgingReport(),
    enabled: gate.tabsVisible,
  })
  const accounts = useQuery({
    queryKey: ['house-accounts', 'list'],
    queryFn: () => apiClient.listHouseAccounts(),
  })
  const statement = useQuery({
    queryKey: ['house-accounts', 'statement', statementFor?.id, range.fromISO, range.toISO],
    queryFn: () => apiClient.getHouseAccountStatement(statementFor!.id, { from: range.fromISO, to: range.toISO }),
    enabled: Boolean(statementFor),
  })

  const download = (fn: () => Promise<void>) => {
    fn().catch((e: unknown) => toastHelpers.error('Export', e instanceof Error ? e.message : 'Export failed'))
  }

  const staffRows = staff.data?.data?.rows ?? []
  const agingRows = aging.data?.data ?? []
  const accountRows = (accounts.data?.data ?? []).filter((a) => (a.kind === 'tab' ? gate.tabsVisible : gate.staff))
  const staffStatement = statementFor?.kind === 'staff_allowance'

  return (
    <div className="space-y-6">
      {gate.staff && (
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <Utensils className="h-4 w-4" aria-hidden /> Staff meals by month
          </CardTitle>
          <div className="flex items-center gap-2">
            {/* F-MM-N31: allowances are given and changed in Manage Staff. */}
            <Button variant="ghost" size="sm" onClick={() => void navigate({ to: '/admin/staff' })}>
              Manage in Staff
            </Button>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" />
            <Button variant="outline" size="sm" onClick={() => download(() => apiClient.exportStaffMonthCsv(month))}>
              <Download className="mr-2 h-4 w-4" aria-hidden /> CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {staff.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : staffRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No staff meals this month.</p>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead className="text-right">Meals</TableHead>
                    <TableHead className="text-right">Allowance</TableHead>
                    <TableHead className="text-right">Consumed</TableHead>
                    <TableHead className="text-right">Covered</TableHead>
                    <TableHead className="text-right">Over</TableHead>
                    <TableHead className="text-right">Food cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffRows.map((r) => (
                    <TableRow key={r.account_id}>
                      <TableCell>
                        <div className="font-medium">{r.person_name ?? r.customer_name}</div>
                        {r.has_login === false ? <div className="text-xs text-muted-foreground">No POS access</div> : null}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.meals}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(r.monthly_allowance)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(r.consumed)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(r.allowance_used)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.overage > 0 ? (
                          <span className="font-semibold text-amber-700 dark:text-amber-400">{formatCurrency(r.overage)}</span>
                        ) : (
                          formatCurrency(0)
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(r.cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {staffRows.some((r) => r.overage_entries.length > 0) ? (
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Meals over the allowance
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Person</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Order</TableHead>
                        <TableHead className="text-right">Bill</TableHead>
                        <TableHead className="text-right">Over</TableHead>
                        <TableHead>Approved by</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {staffRows.flatMap((r) =>
                        r.overage_entries.map((e) => (
                          <TableRow key={`${r.account_id}-${e.order_id}`}>
                            <TableCell>{r.person_name ?? r.customer_name}</TableCell>
                            <TableCell className="tabular-nums">{e.business_date}</TableCell>
                            <TableCell className="tabular-nums">
                              {new Date(e.charged_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                            </TableCell>
                            <TableCell className="font-mono text-xs">{e.order_number}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatCurrency(e.amount)}</TableCell>
                            <TableCell className="text-right tabular-nums">{formatCurrency(e.overage)}</TableCell>
                            <TableCell>{e.approved_by ?? '—'}</TableCell>
                          </TableRow>
                        )),
                      )}
                    </TableBody>
                  </Table>
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {gate.tabsVisible && (
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
          <CardTitle className="flex items-center gap-2 text-base">
            <BookUser className="h-4 w-4" aria-hidden /> Customer tabs — who owes what
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => download(() => apiClient.exportTabAgingCsv())}>
            <Download className="mr-2 h-4 w-4" aria-hidden /> CSV
          </Button>
        </CardHeader>
        <CardContent>
          {aging.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : agingRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tab has an open balance.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guest</TableHead>
                  <TableHead className="text-right">Owes</TableHead>
                  <TableHead className="text-right">0–7 days</TableHead>
                  <TableHead className="text-right">8–30 days</TableHead>
                  <TableHead className="text-right">31+ days</TableHead>
                  <TableHead className="text-right">Limit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agingRows.map((r) => (
                  <TableRow key={r.account_id}>
                    <TableCell>
                      <div className="font-medium">{r.customer_name}</div>
                      {r.customer_phone ? <div className="text-xs text-muted-foreground">{r.customer_phone}</div> : null}
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums">{formatCurrency(r.balance)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(r.days_0_7)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(r.days_8_30)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.days_31_plus > 0 ? (
                        <span className="font-semibold text-destructive">{formatCurrency(r.days_31_plus)}</span>
                      ) : (
                        formatCurrency(0)
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {r.credit_limit > 0 ? formatCurrency(r.credit_limit) : 'None'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">All accounts</CardTitle>
        </CardHeader>
        <CardContent>
          {accounts.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : accountRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No accounts yet. Give staff a meal allowance in Manage Staff, or open a tab on a guest&apos;s profile in
              Customers.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Holder</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">This month / owes</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {accountRows.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.person_name ?? a.customer_name}</TableCell>
                    <TableCell>{a.kind === 'tab' ? 'Tab' : 'Staff meals'}</TableCell>
                    <TableCell>
                      <Badge variant={a.status === 'active' ? 'secondary' : 'outline'}>{a.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {a.kind === 'tab'
                        ? formatCurrency(a.balance)
                        : `${formatCurrency(a.allowance_used)} of ${formatCurrency(a.monthly_allowance)}`}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => setStatementFor(a)}>
                        Statement
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(statementFor)} onOpenChange={(o) => !o && setStatementFor(null)}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{statementFor?.person_name ?? statementFor?.customer_name} — statement</DialogTitle>
            <DialogDescription>
              {range.fromISO} to {range.toISO}
            </DialogDescription>
          </DialogHeader>
          {statement.isLoading || !statement.data?.data ? (
            <Skeleton className="h-32 w-full" />
          ) : (
            <div className="max-h-[60vh] overflow-y-auto">
              {!staffStatement ? (
                <div className="mb-2 flex justify-between text-sm">
                  <span className="text-muted-foreground">Opening balance</span>
                  <span className="tabular-nums">{formatCurrency(statement.data.data.opening_balance)}</span>
                </div>
              ) : null}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Entry</TableHead>
                    <TableHead>Order</TableHead>
                    {staffStatement ? (
                      <>
                        <TableHead className="text-right">Meal value</TableHead>
                        <TableHead className="text-right">Covered</TableHead>
                        <TableHead className="text-right">Over</TableHead>
                      </>
                    ) : (
                      <>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Balance</TableHead>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.data.data.entries.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="tabular-nums">{e.business_date ?? e.created_at.slice(0, 10)}</TableCell>
                      <TableCell>
                        {staffStatement && e.entry_type === 'charge'
                          ? 'Meal'
                          : staffStatement && e.entry_type === 'charge_reversal'
                            ? 'Undone'
                            : ENTRY_LABEL[e.entry_type] ?? e.entry_type}
                        {e.method ? ` · ${e.method}` : ''}
                        {e.over_limit_override ? ' · over limit' : ''}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{e.order_number ?? ''}</TableCell>
                      {staffStatement ? (
                        <>
                          <TableCell className="text-right tabular-nums">{formatCurrency(e.amount)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatCurrency(e.allowance_covered)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {e.overage !== 0 ? formatCurrency(e.overage) : ''}
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell className="text-right tabular-nums">{formatCurrency(e.amount)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatCurrency(e.running_balance)}</TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {!staffStatement ? (
                <div className="mt-2 flex justify-between text-sm font-semibold">
                  <span>Closing balance</span>
                  <span className="tabular-nums">{formatCurrency(statement.data.data.closing_balance)}</span>
                </div>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
